import {computed, inject, Injectable} from '@angular/core';
import {CreditNotePeppolSummary, CreditNoteService, InvoicePeppolSummary, InvoiceService} from '../../../../api';
import {injectQuery, QueryClient} from '@tanstack/angular-query-experimental';
import {lastValueFrom} from 'rxjs';
import {QueryKey} from '../../../constants/query-key';
import {FinancesPeriodStore} from './finances-period.store';

@Injectable()
export class PeppolStoreService {
  private invoiceService = inject(InvoiceService);
  private creditNoteService = inject(CreditNoteService);
  private store = inject(FinancesPeriodStore);
  private readonly queryClient = inject(QueryClient);

  private get _hasDates() {
    const {startDate, endDate} = this.store.period();
    return startDate != null && endDate != null;
  }

  incomingInvoiceQuery = injectQuery(() => ({
    queryKey: [QueryKey.INCOMING_INVOICES, this.store.period().startDate, this.store.period().endDate],
    queryFn: () => lastValueFrom(this.invoiceService.getIncomingInvoices(this.store.period().startDate, this.store.period().endDate)),
    enabled: this._hasDates,
  }));

  outgoingInvoiceQuery = injectQuery(() => ({
    queryKey: [QueryKey.OUTGOING_INVOICES, this.store.period().startDate, this.store.period().endDate],
    queryFn: () => lastValueFrom(this.invoiceService.getOutgoingInvoices(this.store.period().startDate, this.store.period().endDate)),
    enabled: this._hasDates,
  }));

  isIncomingLoading = computed(() => this.incomingInvoiceQuery.isLoading());
  incomingInvoices = computed<InvoicePeppolSummary[]>(() => (this.incomingInvoiceQuery.data()?.invoices as unknown as InvoicePeppolSummary[]) ?? []);

  isOutgoingLoading = computed(() => this.outgoingInvoiceQuery.isLoading());
  outgoingInvoices = computed<InvoicePeppolSummary[]>(() => this.outgoingInvoiceQuery.data()?.invoices ?? []);

  incomingCreditNoteQuery = injectQuery(() => ({
    queryKey: [QueryKey.INCOMING_CREDIT_NOTES, this.store.period().startDate, this.store.period().endDate],
    queryFn: () => lastValueFrom(this.creditNoteService.getIncomingCreditNotes(this.store.period().startDate, this.store.period().endDate)),
    enabled: this._hasDates,
  }));

  outgoingCreditNoteQuery = injectQuery(() => ({
    queryKey: [QueryKey.OUTGOING_CREDIT_NOTES, this.store.period().startDate, this.store.period().endDate],
    queryFn: () => lastValueFrom(this.creditNoteService.getOutgoingCreditNotes(this.store.period().startDate, this.store.period().endDate)),
    enabled: this._hasDates,
  }));

  isIncomingCreditNotesLoading = computed(() => this.incomingCreditNoteQuery.isLoading());
  incomingCreditNotes = computed<CreditNotePeppolSummary[]>(() => this.incomingCreditNoteQuery.data()?.creditNotes ?? []);

  isOutgoingCreditNotesLoading = computed(() => this.outgoingCreditNoteQuery.isLoading());
  outgoingCreditNotes = computed<CreditNotePeppolSummary[]>(() => this.outgoingCreditNoteQuery.data()?.creditNotes ?? []);

  invalidIncoming() {
    this.queryClient.invalidateQueries({queryKey: [QueryKey.INCOMING_INVOICES, this.store.period().startDate, this.store.period().endDate]});
  }

  invalidOutgoing() {
    this.queryClient.invalidateQueries({queryKey: [QueryKey.OUTGOING_INVOICES, this.store.period().startDate, this.store.period().endDate]});
  }

  invalidIncomingCreditNotes() {
    this.queryClient.invalidateQueries({queryKey: [QueryKey.INCOMING_CREDIT_NOTES, this.store.period().startDate, this.store.period().endDate]});
  }

  invalidOutgoingCreditNotes() {
    this.queryClient.invalidateQueries({queryKey: [QueryKey.OUTGOING_CREDIT_NOTES, this.store.period().startDate, this.store.period().endDate]});
  }
}
